import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { AppShell } from './components/AppShell.js';
import { validateObrasSearch } from './features/obras/search.js';
import { AuditoriaPage } from './routes/AuditoriaPage.js';
import { ConfiguracoesPage } from './routes/ConfiguracoesPage.js';
import { DashboardPage } from './routes/DashboardPage.js';
import { EmpresasPage } from './routes/EmpresasPage.js';
import { ConsumoPage } from './routes/ConsumoPage.js';
import { ContratosPage } from './routes/ContratosPage.js';
import { PerfilPage } from './routes/PerfilPage.js';
import { SecretariasPage } from './routes/SecretariasPage.js';
import { LoginPage } from './routes/LoginPage.js';
import { MapaDeObrasPage } from './routes/MapaDeObrasPage.js';
import { NovaObraPage } from './routes/NovaObraPage.js';
import { ObraDetailPage } from './routes/ObraDetailPage.js';
import { ObrasPage } from './routes/ObrasPage.js';
import { PlataformaOrganizacoesPage } from './routes/PlataformaOrganizacoesPage.js';
import { RelatoriosPage } from './routes/RelatoriosPage.js';
import { PlataformaUsuariosPage } from './routes/PlataformaUsuariosPage.js';
import { ForgotPasswordPage, NewPasswordPage } from './routes/PublicAuthPages.js';
import { TarefasPage } from './routes/TarefasPage.js';
import { UsuariosPage } from './routes/UsuariosPage.js';

/**
 * Contexto do roteador: quem está autenticado e onde.
 *
 * Injetado pelo `<App>` a cada render, para que os guardas de rota leiam o
 * estado atual da sessão sem duplicá-lo.
 */
export interface RouterContext {
  readonly isAuthenticated: boolean;
  /** `true` quando a sessão é a da plataforma, que não tem organização. */
  readonly inPlatform: boolean;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="grid min-h-dvh place-items-center bg-bg px-6 text-center">
      <div>
        <p className="font-mono text-[13px] text-ink-3">404</p>
        <h1 className="mt-2 text-[32px] font-semibold tracking-tight">Página não encontrada</h1>
        <p className="mt-2 text-ink-3">O endereço acessado não existe no sistema.</p>
        <a
          href="/"
          className="mt-6 inline-flex h-10 items-center rounded-md bg-ink px-4 font-medium text-on-ink"
        >
          Voltar ao início
        </a>
      </div>
    </div>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: ({ context }) => {
    if (context.isAuthenticated) throw redirect({ to: context.inPlatform ? '/plataforma' : '/' });
  },
});

/*
 * Telas de credencial. Quem chega aqui ainda não tem sessão, e mesmo quem tem
 * pode precisar delas — trocar a própria senha por um link, por exemplo — então
 * não redirecionam quando já autenticado.
 */
const newPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nova-senha',
  component: NewPasswordPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recuperar-senha',
  component: ForgotPasswordPage,
});

/**
 * A casca — trilho, barra superior, transição de página — vive na rota-mãe,
 * não em cada página. É o que mantém a barra lateral montada de uma rota para
 * a outra: o indicador do item ativo desliza em vez de reaparecer, e o menu
 * não refaz a leitura dos contadores a cada clique.
 */
function Shell() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * Rota-mãe do painel de um órgão.
 *
 * A sessão da plataforma não tem organização, então não tem o que ver aqui —
 * é mandada para a própria área.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: Shell,
  beforeLoad: ({ context }) => {
    if (!context.isAuthenticated) throw redirect({ to: '/login' });
    if (context.inPlatform) throw redirect({ to: '/plataforma' });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardPage,
});

/** A lista aceita um recorte pela URL — é como o painel a abre já filtrada. */
const obrasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/obras',
  component: ObrasPage,
  validateSearch: validateObrasSearch,
});

/**
 * O cadastro de obra, em etapas, numa página própria. Segmento fixo: o roteador
 * o prefere ao `$obraId`, e "nova" nunca é lida como o id de uma obra.
 */
const novaObraRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/obras/nova',
  component: NovaObraPage,
});

const obraDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/obras/$obraId',
  component: ObraDetailPage,
});

/**
 * O Mapa de Obras: o acervo sobre o mapa, numa tela só dele — e de tela cheia:
 * a casca entrega a área de conteúdo inteira, sem respiro nem largura máxima.
 */
const mapaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/mapa',
  component: MapaDeObrasPage,
  staticData: { telaCheia: true },
});

/**
 * Contratos: a planilha de acompanhamento dos contratos das obras. Só lê — o
 * contrato é o da obra, e se corrige nela.
 */
const contratosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/contratos',
  component: ContratosPage,
});

const empresasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/empresas',
  component: EmpresasPage,
});

const consumoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/consumo',
  component: ConsumoPage,
});

const perfilRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/perfil',
  component: PerfilPage,
});

const secretariasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/secretarias',
  component: SecretariasPage,
});

const auditoriaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/auditoria',
  component: AuditoriaPage,
});

const relatoriosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/relatorios',
  component: RelatoriosPage,
});

const tarefasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/tarefas',
  component: TarefasPage,
});

const configuracoesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/configuracoes',
  component: ConfiguracoesPage,
});

const usuariosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/usuarios',
  component: UsuariosPage,
});

/** Rota-mãe da área da plataforma, fora de qualquer organização. */
const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'platform',
  component: Shell,
  beforeLoad: ({ context }) => {
    if (!context.isAuthenticated) throw redirect({ to: '/login' });
    if (!context.inPlatform) throw redirect({ to: '/' });
  },
});

const platformOrganizationsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/plataforma',
  component: PlataformaOrganizacoesPage,
});

const platformUsersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/plataforma/usuarios',
  component: PlataformaUsuariosPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  newPasswordRoute,
  forgotPasswordRoute,
  platformRoute.addChildren([platformOrganizationsRoute, platformUsersRoute]),
  protectedRoute.addChildren([
    dashboardRoute,
    obrasRoute,
    novaObraRoute,
    obraDetailRoute,
    mapaRoute,
    contratosRoute,
    tarefasRoute,
    relatoriosRoute,
    auditoriaRoute,
    empresasRoute,
    secretariasRoute,
    perfilRoute,
    consumoRoute,
    usuariosRoute,
    configuracoesRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { isAuthenticated: false, inPlatform: false },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  interface StaticDataRouteOption {
    /**
     * A página ocupa a área de conteúdo inteira, colada nas bordas e da altura
     * da janela, sem o respiro nem a largura máxima das outras (ver `AppShell`).
     */
    telaCheia?: boolean;
  }
}
